import React from 'react'

// Accent color configurations for different widget types
export const WIDGET_ACCENTS = {
  pipeline: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-gray-900 dark:text-gray-600',
    border: 'border-blue-200 dark:border-gray-900'
  },
  funded: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-600'
  },
  revenue: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-600'
  },
  paid: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-gray-900 dark:text-gray-600',
    border: 'border-purple-200 dark:border-gray-900'
  },
  tasks: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-600'
  },
  announcements: {
    bg: 'bg-pink-50 dark:bg-pink-900/20',
    text: 'text-pink-600 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-600'
  },
  analytics: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-600'
  }
} as const

export type WidgetAccentType = keyof typeof WIDGET_ACCENTS
export type WidgetVariant = 'default' | 'colored' | 'gradient'

interface BaseWidgetProps {
  title: string
  icon?: React.ReactNode
  accent: WidgetAccentType
  variant?: WidgetVariant
  className?: string
  children: React.ReactNode
  actions?: React.ReactNode
  footer?: React.ReactNode
  loading?: boolean
  minimalDisplay?: boolean // When true, hide footer and optimize for compact view
}

interface LoadingSkeletonProps {
  accent: WidgetAccentType
  variant?: WidgetVariant
  className?: string
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ accent, variant = 'default', className }) => {
  const accentConfig = WIDGET_ACCENTS[accent]
  
  const getBackgroundClasses = () => {
    switch (variant) {
      case 'colored':
        return `${accentConfig.bg} border ${accentConfig.border}`
      case 'gradient':
        return getGradientClasses(accent)
      default:
        return 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
    }
  }
  
  return (
    <div className={`
      ${getBackgroundClasses()}
      rounded-xl 
      shadow-sm 
      p-6 
      h-full 
      flex flex-col
      ${className || ''}
    `}>
      <div className="flex items-center space-x-3 mb-4">
        <div className={`p-2 rounded-lg ${variant === 'default' ? accentConfig.bg : 'bg-white/20'} animate-pulse`}>
          <div className={`h-5 w-5 rounded ${variant === 'default' ? 'bg-gray-300 dark:bg-gray-600' : 'bg-white/40'}`}></div>
        </div>
        <div className={`h-6 rounded w-32 animate-pulse ${variant === 'default' ? 'bg-gray-300 dark:bg-gray-600' : 'bg-white/20'}`}></div>
      </div>
      
      <div className="flex-1 space-y-3">
        <div className={`h-8 rounded w-24 animate-pulse ${variant === 'default' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-white/10'}`}></div>
        <div className={`h-4 rounded w-40 animate-pulse ${variant === 'default' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-white/10'}`}></div>
        <div className={`h-4 rounded w-32 animate-pulse ${variant === 'default' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-white/10'}`}></div>
      </div>
    </div>
  )
}

const getGradientClasses = (accent: WidgetAccentType) => {
  switch (accent) {
    case 'pipeline':
      return 'bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 text-white border-blue-500'
    case 'funded':
      return 'bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 text-white border-green-500'
    case 'revenue':
      return 'bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 text-white border-orange-500'
    case 'paid':
      return 'bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 text-white border-purple-500'
    case 'announcements':
      return 'bg-gradient-to-br from-pink-500 via-purple-600 to-violet-600 text-white border-pink-500'
    case 'analytics':
      return 'bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-600 text-white border-emerald-500'
    case 'tasks':
      return 'bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 text-white border-indigo-500'
    default:
      return 'bg-gradient-to-br from-gray-500 via-gray-600 to-slate-600 text-white border-gray-500'
  }
}

export const BaseWidget: React.FC<BaseWidgetProps> = ({
  title,
  icon,
  accent,
  variant = 'default',
  className,
  children,
  actions,
  footer,
  loading = false,
  minimalDisplay = false
}) => {
  const accentConfig = WIDGET_ACCENTS[accent]

  if (loading) {
    return <LoadingSkeleton accent={accent} variant={variant} className={className} />
  }

  const getBackgroundClasses = () => {
    switch (variant) {
      case 'colored':
        return `${accentConfig.bg} border ${accentConfig.border}`
      case 'gradient':
        return getGradientClasses(accent)
      default:
        return 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
    }
  }

  const getTextClasses = () => {
    switch (variant) {
      case 'colored':
        return {
          title: `text-gray-900 dark:text-white`,
          icon: accentConfig.text
        }
      case 'gradient':
        return {
          title: 'text-white',
          icon: 'text-white'
        }
      default:
        return {
          title: 'text-gray-900 dark:text-white',
          icon: accentConfig.text
        }
    }
  }

  const getIconBackground = () => {
    switch (variant) {
      case 'gradient':
        return 'bg-white/20'
      case 'colored':
        return 'bg-white dark:bg-gray-800'
      default:
        return accentConfig.bg
    }
  }

  const getBorderClasses = () => {
    if (variant === 'gradient' || variant === 'colored') {
      return ''
    }
    return 'border-t border-gray-100 dark:border-gray-700'
  }

  const textClasses = getTextClasses()

  return (
    <div className={`
      ${getBackgroundClasses()}
      rounded-xl 
      shadow-sm 
      p-4
      h-full 
      flex flex-col
      ${className || ''}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          {icon && (
            <div className={`p-1.5 rounded-lg flex-shrink-0 ${getIconBackground()}`}>
              <div className={textClasses.icon}>
                {icon}
              </div>
            </div>
          )}
          <h3 className={`text-base font-semibold truncate ${textClasses.title}`}>
            {title}
          </h3>
        </div>
        {actions && (
          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
            {actions}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {children}
      </div>

      {/* Footer - Hidden in minimal display mode */}
      {footer && !minimalDisplay && (
        <div className={`mt-3 pt-3 flex-shrink-0 ${getBorderClasses()}`}>
          {footer}
        </div>
      )}
    </div>
  )
}

// Utility components for common widget patterns

interface MetricCardProps {
  label: string
  value: string | number
  subValue?: string
  accent?: WidgetAccentType
  variant?: 'default' | 'gradient' | 'transparent'
  className?: string
}

export const MetricCard: React.FC<MetricCardProps> = ({ 
  label, 
  value, 
  subValue, 
  accent,
  variant = 'default',
  className 
}) => {
  const getBackgroundClass = () => {
    switch (variant) {
      case 'gradient':
        return 'bg-white/10'
      case 'transparent':
        return 'bg-transparent'
      default:
        return accent ? WIDGET_ACCENTS[accent].bg : 'bg-gray-50 dark:bg-gray-700'
    }
  }

  const getTextClasses = () => {
    switch (variant) {
      case 'gradient':
        return {
          label: 'text-white/80',
          value: 'text-white',
          subValue: 'text-white/60'
        }
      case 'transparent':
        return {
          label: 'text-gray-600 dark:text-gray-300',
          value: 'text-gray-900 dark:text-white',
          subValue: 'text-gray-500 dark:text-gray-400'
        }
      default:
        return {
          label: 'text-gray-600 dark:text-gray-300',
          value: 'text-gray-900 dark:text-white',
          subValue: 'text-gray-500 dark:text-gray-400'
        }
    }
  }

  const textClasses = getTextClasses()
  
  return (
    <div className={`${getBackgroundClass()} rounded-lg ${className?.includes('p-') ? '' : 'p-4'} ${className || ''}`}>
      <p className={`text-sm mb-1 ${textClasses.label}`}>
        {label}
      </p>
      <p className={`text-xl font-bold tabular-nums ${textClasses.value}`}>
        {value}
      </p>
      {subValue && (
        <p className={`text-xs mt-1 ${textClasses.subValue}`}>
          {subValue}
        </p>
      )}
    </div>
  )
}

interface ComparisonIndicatorProps {
  change: number
  label?: string
  className?: string
}

export const ComparisonIndicator: React.FC<ComparisonIndicatorProps> = ({ 
  change, 
  label = "vs last year",
  className 
}) => {
  const isPositive = change > 0
  const isNeutral = change === 0
  
  const colorClass = isNeutral 
    ? 'text-gray-500 dark:text-gray-400'
    : isPositive 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400'
  
  const symbol = isNeutral ? '' : isPositive ? '↗' : '↘'
  const sign = isNeutral ? '' : isPositive ? '+' : ''
  
  return (
    <div className={`flex items-center space-x-2 ${className || ''}`}>
      <span className={`text-sm font-medium ${colorClass}`}>
        {symbol} {sign}{change.toFixed(1)}%
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {label}
      </span>
    </div>
  )
}

interface ProgressBarProps {
  percentage: number
  accent?: WidgetAccentType
  className?: string
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ 
  percentage, 
  accent = 'pipeline',
  className 
}) => {
  const progressColor = WIDGET_ACCENTS[accent].text.replace('text-', 'bg-')
  
  return (
    <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 ${className || ''}`}>
      <div 
        className={`${progressColor} h-2 rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
      />
    </div>
  )
}

export default BaseWidget